import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../AuthProvider";
import { Section, ACCENT } from "../cuentas/shared";
import { Kpi, haceEnDias } from "./shared";
import { usoPorUsuario, usoPorAccion } from "../../utils/usoPlataforma";
import { getActionLabel } from "../../utils/bitacora";
import {
  UsersIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/20/solid";

// Quién usa el panel y quién no, usuario por usuario. Es el reporte que
// contesta la pregunta de renovación: un owner que no entró en dos meses no
// va a renovar, y hoy eso solo se sabía preguntando.
//
// Solo admins (lo gatea el registry). Lee de dos colecciones que ninguna
// pantalla de venue toca: `userActivity` (horas con el panel abierto) y la
// bitácora de TODOS los venues por collection-group.
//
// AdminStats mide lo mismo pero por venue; aquí el sujeto es la persona.

const VENTANAS = [
  { key: "30", label: "30 días", dias: 30 },
  { key: "60", label: "60 días", dias: 60 },
  { key: "90", label: "90 días", dias: 90 },
];

const ESTADO_STYLE = {
  activo: { label: "Activo", className: "text-emerald-600" },
  tibio: { label: "Intermitente", className: "text-amber-600" },
  inactivo: { label: "Inactivo", className: "text-red-600" },
  nunca: { label: "Sin registro", className: "text-zinc-400" },
};

const fmtUltima = (fecha) => {
  if (!fecha) return "—";
  return fecha.toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ReporteUsoPlataforma = () => {
  const { userInfo } = useAuth();
  const [ventana, setVentana] = useState("30");
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const [soloProblemas, setSoloProblemas] = useState(false);
  // "" = todos los venues.
  const [venueFiltro, setVenueFiltro] = useState("");

  const dias = VENTANAS.find((v) => v.key === ventana)?.dias ?? 30;
  const esAdmin = userInfo?.role === "admin";

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const desde = Timestamp.fromDate(new Date(Date.now() - dias * 86400000));
      const [usersSnap, venuesSnap, actividadSnap, bitacoraSnap] =
        await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "venues")),
          getDocs(
            query(collection(db, "userActivity"), where("lastSeen", ">=", desde))
          ),
          getDocs(
            query(collectionGroup(db, "bitacora"), where("timestamp", ">=", desde))
          ),
        ]);

      setDatos({
        users: usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        venues: venuesSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        actividad: actividadSnap.docs.map((d) => d.data()),
        // La subcolección no guarda el venue: se resuelve del padre, igual
        // que en AdminStats.
        bitacora: bitacoraSnap.docs.map((d) => ({
          ...d.data(),
          venueId: d.ref.parent.parent?.id || null,
        })),
      });
    } catch (err) {
      console.error("Uso de la plataforma:", err);
      setError(err.message || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [dias]);

  useEffect(() => {
    if (!esAdmin) return;
    cargar();
  }, [cargar, esAdmin, tick]);

  // El filtro se aplica ANTES de agregar, no sobre el resultado: si se
  // filtraran las filas ya sumadas, las horas y acciones de una persona
  // seguirían siendo las de TODOS sus venues y el reporte mentiría al
  // acotarlo a uno.
  const filtrados = useMemo(() => {
    if (!datos) return null;
    if (!venueFiltro) return datos;
    return {
      ...datos,
      users: datos.users.filter((u) =>
        Array.isArray(u.venues) ? u.venues.includes(venueFiltro) : false
      ),
      actividad: datos.actividad.filter((a) => a.venueId === venueFiltro),
      bitacora: datos.bitacora.filter((b) => b.venueId === venueFiltro),
    };
  }, [datos, venueFiltro]);

  const uso = useMemo(
    () =>
      filtrados
        ? usoPorUsuario({
            users: filtrados.users,
            actividad: filtrados.actividad,
            bitacora: filtrados.bitacora,
            venues: filtrados.venues,
          })
        : null,
    [filtrados]
  );

  const acciones = useMemo(
    () => (filtrados ? usoPorAccion(filtrados.bitacora).slice(0, 8) : []),
    [filtrados]
  );

  const venuesOrdenados = useMemo(
    () =>
      [...(datos?.venues || [])].sort((a, b) =>
        (a.name || a.id).localeCompare(b.name || b.id)
      ),
    [datos]
  );

  if (!esAdmin) {
    return (
      <Section icon={ShieldCheckIcon} title="Uso de la plataforma">
        <p className="text-sm text-zinc-400">Solo para administradores.</p>
      </Section>
    );
  }

  const filas = (uso?.filas || []).filter((f) =>
    soloProblemas ? f.estado === "inactivo" || f.estado === "nunca" : true
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {VENTANAS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => setVentana(v.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              ventana === v.key
                ? "text-white shadow-sm"
                : "text-zinc-500 bg-zinc-100 dark:bg-zinc-700/60 hover:text-zinc-700"
            }`}
            style={ventana === v.key ? { backgroundColor: ACCENT } : undefined}
          >
            {v.label}
          </button>
        ))}
        {venuesOrdenados.length > 0 && (
          <select
            value={venueFiltro}
            onChange={(e) => setVenueFiltro(e.target.value)}
            className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2.5 py-1.5 text-xs font-semibold text-zinc-600 dark:text-zinc-300 max-w-[220px]"
          >
            <option value="">Todos los venues</option>
            {venuesOrdenados.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name || v.id}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => setTick((t) => t + 1)}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
          <div className="mt-1 text-xs opacity-80">
            Si la consulta pide un índice, Firestore manda el link en la
            consola del navegador.
          </div>
        </div>
      )}

      {loading && !uso ? (
        <Section icon={UsersIcon} title="Uso de la plataforma">
          <p className="text-sm text-zinc-400">Cargando actividad…</p>
        </Section>
      ) : uso ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi
              label="Activos"
              value={uso.totales.activos}
              hint="con señal en los últimos 7 días"
              tone="text-emerald-600"
            />
            <Kpi
              label="Intermitentes"
              value={uso.totales.tibios}
              hint="entre 8 y 30 días sin entrar"
              tone={uso.totales.tibios > 0 ? "text-amber-600" : ""}
            />
            <Kpi
              label="Inactivos"
              value={uso.totales.inactivos}
              hint="más de 30 días sin señal"
              tone={uso.totales.inactivos > 0 ? "text-red-600" : ""}
            />
            <Kpi
              label="Sin registro"
              value={uso.totales.nunca}
              hint={`en la ventana de ${dias} días`}
            />
            <Kpi
              label="Horas con el panel"
              value={uso.totales.horas}
              hint={`${uso.totales.acciones} acciones registradas`}
            />
          </div>

          <Section
            icon={UsersIcon}
            title={
              venueFiltro
                ? `Usuario por usuario · ${
                    venuesOrdenados.find((v) => v.id === venueFiltro)?.name || venueFiltro
                  }`
                : "Usuario por usuario"
            }
            action={
              <button
                type="button"
                onClick={() => setSoloProblemas((v) => !v)}
                className="text-xs font-semibold hover:underline"
                style={{ color: ACCENT }}
              >
                {soloProblemas ? "Ver todos" : "Solo los que no entran"}
              </button>
            }
          >
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
                    <th className="py-2 pl-1 font-semibold">Usuario</th>
                    <th className="py-2 font-semibold">Rol</th>
                    <th className="py-2 font-semibold">Venues</th>
                    <th className="py-2 font-semibold text-right">Horas</th>
                    <th className="py-2 font-semibold text-right">Acciones</th>
                    <th className="py-2 pr-1 font-semibold text-right">
                      Última actividad
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/50">
                  {filas.map((f) => {
                    const estado = ESTADO_STYLE[f.estado];
                    return (
                      <tr key={f.id}>
                        <td className="py-2 pl-1">
                          <div className="font-medium text-zinc-900 dark:text-white truncate max-w-[200px]">
                            {f.nombre || f.correo || f.id}
                            {f.huerfano && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-zinc-400">
                                sin cuenta
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-400 truncate max-w-[200px]">
                            {f.correo}
                          </div>
                        </td>
                        <td className="py-2 text-xs text-zinc-500 whitespace-nowrap">
                          {f.rol || "—"}
                        </td>
                        <td className="py-2 text-xs text-zinc-500">
                          <span className="block truncate max-w-[180px]" title={f.venues.join(", ")}>
                            {f.venues.length > 0 ? f.venues.join(", ") : "—"}
                          </span>
                        </td>
                        <td className="py-2 text-right text-zinc-500 whitespace-nowrap">
                          {f.horas || "—"}
                        </td>
                        <td className="py-2 text-right text-zinc-500 whitespace-nowrap">
                          {f.acciones || "—"}
                        </td>
                        <td className="py-2 pr-1 text-right whitespace-nowrap">
                          <div className={`font-medium ${estado.className}`}>
                            {f.sinDatosEsperado && f.estado === "nunca"
                              ? "No se mide"
                              : estado.label}
                          </div>
                          <div className="text-xs text-zinc-400">
                            {f.ultima
                              ? `${fmtUltima(f.ultima)} · ${haceEnDias(f.ultima)}`
                              : f.sinDatosEsperado
                              ? "los admins no dejan rastro"
                              : "sin señal en la ventana"}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-zinc-400">
              Dos señales: las horas cuentan los bloques de una hora con el
              panel abierto (aunque no se toque nada) y las acciones son lo que
              quedó en la bitácora. Los administradores están excluidos de las
              dos a propósito, así que siempre saldrán sin registro — su fila
              dice "No se mide", no "inactivo".
            </p>
          </Section>

          <Section icon={ClipboardDocumentListIcon} title="Acciones más frecuentes">
            {acciones.length === 0 ? (
              <p className="text-sm text-zinc-400">
                Sin acciones registradas en la ventana.
              </p>
            ) : (
              <div className="space-y-2.5">
                {acciones.map((fam) => {
                  const top = Object.entries(fam.acciones)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3);
                  const max = acciones[0].total || 1;
                  return (
                    <div key={fam.familia}>
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="text-zinc-600 dark:text-zinc-300 truncate">
                          {top.map(([a]) => getActionLabel(a)).join(" · ")}
                        </span>
                        <span className="font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                          {fam.total}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(fam.total / max) * 100}%`,
                            backgroundColor: ACCENT,
                            opacity: 0.8,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </>
      ) : null}
    </div>
  );
};

export default ReporteUsoPlataforma;
